// gestureFields — the ONE place that reads a gesture row's fields off whatever
// casing the server sent.
//
// A gesture row reaches the client from `GET /api/characters/{slug}/gestures`
// as `GestureLinkDTO`, which emits the two ASSET IDS in camelCase ONLY
// (`bodyAnimationId` / `faceAnimationId`) while mirroring `display_name`,
// `duration_ms`, `body_weight` and the resolved urls in snake_case. Every
// reader that hardcoded one casing has been wrong at least once:
//
//   - FaceClipPlayer.loadAll read the snake ids and nulled out every gesture
//     mapping when the server moved to camelCase (ENG-598). Fixed there by
//     accepting both.
//   - GestureDetail (the Composer's gesture editor) read the snake ids, which
//     the DTO has never emitted — so every existing gesture opened with both
//     animation dropdowns blank and "Save Gesture" looked like it wiped them.
//
// Two readers, one rule, so the next server-side rename breaks neither. Accept
// BOTH casings in BOTH directions: a field the server stops sending must never
// silently read as "none".

export const DEFAULT_DURATION_MS = 3000
export const DEFAULT_BODY_WEIGHT = 0.7

/** The body/face animation_asset ids on a gesture row, either casing. */
export function gestureAnimationIds(row) {
  return {
    bodyAnimationId: row?.bodyAnimationId ?? row?.body_animation_id ?? null,
    faceAnimationId: row?.faceAnimationId ?? row?.face_animation_id ?? null,
  }
}

/** A gesture row read into the Composer editor's form shape. */
export function readGestureFields(row) {
  const { bodyAnimationId, faceAnimationId } = gestureAnimationIds(row)
  return {
    tag:         row?.tag ?? '',
    displayName: row?.display_name ?? row?.displayName ?? '',
    bodyAnimationId,
    faceAnimationId,
    durationMs:  row?.duration_ms ?? row?.durationMs ?? DEFAULT_DURATION_MS,
    bodyWeight:  row?.body_weight ?? row?.bodyWeight ?? DEFAULT_BODY_WEIGHT,
  }
}

/**
 * A gesture row read into the runtime mapping FaceClipPlayer._gestures holds.
 *
 * The runtime plays clips BY NAME (AvatarModel registers every animation_asset
 * in __AVATAR_ACTIONS__ under its asset.name; FaceClipPlayer stores face clips
 * the same way), so the row's asset ids are resolved through `nameById`.
 *
 * @param {object} row       a gestures-endpoint row
 * @param {Map}    nameById  animation_asset id -> asset.name
 * @returns {{key: string, entry: object}|null} null for a row with no tag —
 *   it can never be looked up, so it is not a mapping.
 */
export function gestureRuntimeEntry(row, nameById) {
  const key = String(row?.tag ?? '').toLowerCase()
  if (!key) return null
  const { bodyAnimationId, faceAnimationId } = gestureAnimationIds(row)
  return {
    key,
    entry: {
      face: faceAnimationId ? (nameById?.get(faceAnimationId) ?? null) : null,
      body: bodyAnimationId ? (nameById?.get(bodyAnimationId) ?? null) : null,
      duration:   row?.duration_ms ?? row?.durationMs ?? DEFAULT_DURATION_MS,
      bodyWeight: row?.body_weight ?? row?.bodyWeight ?? DEFAULT_BODY_WEIGHT,
    },
  }
}

/** Build the whole `_gestures` map from a row list + the animation assets. */
export function buildGestureMap(rows, assets) {
  const nameById = new Map((assets ?? []).map(a => [a.id, a.name]))
  const map = {}
  for (const row of rows ?? []) {
    const built = gestureRuntimeEntry(row, nameById)
    if (built) map[built.key] = built.entry
  }
  return map
}
